import { WORK_ORDER_SETTLE_MS } from '@turn-connect/shared';

/**
 * workOrderEvents — app-wide "a work order was created" announcement.
 *
 * The create-work-order modal is mounted once at App level and opens over
 * whatever view is showing, so the view underneath no longer participates
 * in the create flow and cannot refresh itself through props. Instead the
 * modal ANNOUNCES the creation here and each view that displays
 * work-order-derived data subscribes and applies its own refresh policy
 * (location dashboard invalidates its React Query; the work-order list
 * reloads its hand-rolled fetch). The announcer knows nothing about the
 * consumers and new consumers need no App changes.
 *
 * IMPORTANT — the refresh delay: department counts and routing are applied
 * by ASYNC event listeners on the backend (the create response returns
 * before they run, typically within ~500ms). A refetch fired immediately
 * on creation reads the PRE-routing state and looks exactly like "the
 * count didn't update". Subscribers must wait ROUTING_SETTLE_MS before
 * refetching; `onWorkOrderCreated` applies that delay for them.
 */

export const WORK_ORDER_CREATED_EVENT = 'workorder:created';

/**
 * Backend listeners (routing, auto-close, department counts) settle within
 * ~500ms of the response. The number itself lives in @turn-connect/shared so
 * mobile — which has the same race on the same endpoints — cannot drift from
 * it; see WORK_ORDER_SETTLE_MS for why the wait is needed at all.
 */
export const ROUTING_SETTLE_MS = WORK_ORDER_SETTLE_MS;

export interface WorkOrderCreatedDetail {
  workOrderId: string;
  locationId: string;
  departmentId?: string;
}

export function announceWorkOrderCreated(detail: WorkOrderCreatedDetail): void {
  window.dispatchEvent(new CustomEvent<WorkOrderCreatedDetail>(WORK_ORDER_CREATED_EVENT, { detail }));
}

/**
 * Subscribe to work-order creations. The handler fires ROUTING_SETTLE_MS
 * after the announcement so backend listeners have applied routing and
 * counts by the time the subscriber refetches. Returns an unsubscribe
 * function; pending delayed handlers are cancelled on unsubscribe.
 */
export function onWorkOrderCreated(handler: (detail: WorkOrderCreatedDetail) => void): () => void {
  return subscribeDelayed(WORK_ORDER_CREATED_EVENT, handler);
}

/**
 * "A work order was closed" — fired on explicit close AND when completing the
 * final service auto-closes the work order. The backend applies the close via
 * an ASYNC listener (the completion response returns before the state commits,
 * same ~500ms class as routing), so subscribers get the same settle delay:
 * an immediate refetch would read the still-open work order and the dashboard
 * count would "not update".
 */
export const WORK_ORDER_CLOSED_EVENT = 'workorder:closed';

export interface WorkOrderClosedDetail {
  workOrderId: string;
  locationId: string;
}

export function announceWorkOrderClosed(detail: WorkOrderClosedDetail): void {
  window.dispatchEvent(new CustomEvent<WorkOrderClosedDetail>(WORK_ORDER_CLOSED_EVENT, { detail }));
}

/** Subscribe to work-order closes; handler fires after ROUTING_SETTLE_MS. */
export function onWorkOrderClosed(handler: (detail: WorkOrderClosedDetail) => void): () => void {
  return subscribeDelayed(WORK_ORDER_CLOSED_EVENT, handler);
}

/** Shared delayed-subscription plumbing for the announcements above. */
function subscribeDelayed<T>(eventName: string, handler: (detail: T) => void): () => void {
  const timers = new Set<ReturnType<typeof setTimeout>>();
  const listener = (e: Event) => {
    const detail = (e as CustomEvent<T>).detail;
    if (!detail) return;
    const timer = setTimeout(() => {
      timers.delete(timer);
      handler(detail);
    }, ROUTING_SETTLE_MS);
    timers.add(timer);
  };
  window.addEventListener(eventName, listener);
  return () => {
    window.removeEventListener(eventName, listener);
    timers.forEach((t) => clearTimeout(t));
    timers.clear();
  };
}

/**
 * "Something changed that could move a dashboard number."
 *
 * The created/closed announcements above are explicit: a call site names the
 * event. That works, but it only covers what someone remembered to announce —
 * which is why the location dashboard went stale on adding a service,
 * approving, completing a non-final service, moving departments, editing a
 * price, deleting and reopening. Every one of those moves a count nobody was
 * telling the dashboard about.
 *
 * So this one is announced centrally, from the fetch wrapper in index.tsx, for
 * ANY successful mutating request to a work-order-shaped endpoint. That covers
 * every mutation the app can make today and every one added later, without a
 * call site having to remember anything — the failure mode that produced the
 * bug cannot recur.
 *
 * locationId is best-effort: a URL does not always carry one. Subscribers must
 * treat "absent" as "might be mine" and refresh, because a wasted refetch is
 * cheap and a missed one is the bug being fixed.
 */
export const WORK_ORDER_CHANGED_EVENT = 'workorder:changed';

export interface WorkOrderChangedDetail {
  /** Absent when the URL carried no location — subscribers should not filter it out. */
  locationId?: string;
  /** The request that caused it, for debugging. Never used for routing decisions. */
  path?: string;
}

export function announceWorkOrderChanged(detail: WorkOrderChangedDetail = {}): void {
  window.dispatchEvent(new CustomEvent<WorkOrderChangedDetail>(WORK_ORDER_CHANGED_EVENT, { detail }));
}

/**
 * Subscribe to work-order changes.
 *
 * Coalesced as well as delayed. One user action routinely produces several
 * requests — adding a service writes the service, then re-prices, then reloads
 * the order — and each would otherwise trigger its own refetch storm against
 * the same queries. The handler fires once per burst, ROUTING_SETTLE_MS after
 * the LAST request in it, which also gives the backend's async listeners time
 * to land.
 */
export function onWorkOrderChanged(handler: (detail: WorkOrderChangedDetail) => void): () => void {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let latest: WorkOrderChangedDetail = {};

  const listener = (e: Event) => {
    const detail = (e as CustomEvent<WorkOrderChangedDetail>).detail;
    latest = detail ?? {};
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      handler(latest);
    }, ROUTING_SETTLE_MS);
  };

  window.addEventListener(WORK_ORDER_CHANGED_EVENT, listener);
  return () => {
    window.removeEventListener(WORK_ORDER_CHANGED_EVENT, listener);
    if (timer) clearTimeout(timer);
  };
}
